import { useState, useEffect, useCallback, useMemo } from 'react';
import { CONVEYANCE_TYPES, type Location, type Strings } from '@koala/sdk';
import { GoogleMap, useJsApiLoader, InfoWindow, type Libraries } from '@react-google-maps/api';
import { useTheme } from 'styled-components';
import { type IBasketFulfillment } from '@/types/fulfillment';
import { settings } from '@/components/locations/map/google/map-config';
import {
  StyledMap,
  StyledMapOuter,
  StyledMapSearchButton,
} from '@/components/locations/map/styles';
import { mapTooltip } from '@/components/locations/map/tooltip';

const googleMapLibraries: Libraries = ['places', 'marker'];
interface IMap {
  locations: Location[];
  activeLocationId: number | null;
  setActiveLocation: (id: number) => void;
  toggleFulfillmentModal: (show: boolean, location: Location, conveyanceType: string) => void;
  fetchCenterCoords: (center: number[], radius: number) => void;
  strings: Strings;
  basketFulfillment: IBasketFulfillment;
}

const LocationsMap = ({
  locations,
  activeLocationId,
  setActiveLocation,
  toggleFulfillmentModal,
  fetchCenterCoords,
  strings,
  basketFulfillment,
}: IMap) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [bounds, setBounds] = useState<google.maps.LatLngBounds | null>(null);
  const [showSearchAreaButton, setShowSearchAreaButton] = useState(false);
  const theme = useTheme();
  const mapOptions = {
    ...settings,
    disableDefaultUI: true,
  };
  delete mapOptions.center;
  delete mapOptions.zoom;

  const onLoad = useCallback((map: google.maps.Map) => setMap(map), []);
  const onMount = useCallback(() => setMap(null), []);

  const activeLocation = locations?.find((location) => location.id === activeLocationId);

  const locationMarkers = useMemo(
    () =>
      locations?.length
        ? locations.map((location) => {
            return {
              id: location.id,
              label: location.label,
              position: {
                lat: location.latitude,
                lng: location.longitude,
              },
            };
          })
        : [],
    [locations],
  );

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY ?? '',
    libraries: googleMapLibraries,
  });

  const handleBoundsChanged = () => {
    if (map) {
      setBounds(map.getBounds() ?? null);
    }
  };

  const handleSearchArea = () => {
    if (!bounds) {
      return;
    }

    const sw = bounds.getSouthWest(); // Bottom-left corner
    const mapCenter = map?.getCenter(); // Center

    if (!mapCenter?.lat() || !mapCenter?.lng() || !sw.lat() || !sw.lng()) {
      return;
    }

    // Approximate degrees to miles
    const horizontalDistance = (mapCenter?.lng() - sw.lng()) * 69.2;
    const verticalDistance = (mapCenter?.lat() - sw.lat()) * 69.2;
    const largestRadialDistance: number = Math.max(horizontalDistance, verticalDistance);

    fetchCenterCoords([mapCenter?.lat(), mapCenter?.lng()], largestRadialDistance);

    setShowSearchAreaButton(false);
  };

  useEffect(() => {
    if (map && locationMarkers.length) {
      const bounds = new google.maps.LatLngBounds();
      locationMarkers.forEach((marker) => {
        bounds.extend(marker.position);
      });
      map.fitBounds(bounds);
    }
  }, [map, locationMarkers]);

  useEffect(() => {
    const initAdvancedMarker = () => {
      locationMarkers.forEach((marker) => {
        const advancedMarker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position: { lat: marker.position.lat, lng: marker.position.lng },
        });
        advancedMarker.addListener('gmp-click', () => {
          setActiveLocation(marker.id);
        });
      });
    };
    if (isLoaded && locationMarkers?.length) {
      initAdvancedMarker();
    }
  }, [isLoaded, locationMarkers]);

  return (
    <>
      {isLoaded && !window.matchMedia(`(max-width: 767px)`).matches ? (
        <StyledMapOuter>
          {showSearchAreaButton && (
            <StyledMapSearchButton onClick={handleSearchArea} $buttonTheme="secondary">
              Search This Area
            </StyledMapSearchButton>
          )}
          <StyledMap>
            <GoogleMap
              mapContainerStyle={{
                width: '100%',
                height: '100%',
              }}
              center={settings.center as google.maps.LatLngLiteral}
              zoom={settings.zoom ?? 10}
              options={{
                ...mapOptions,
                mapId: process.env.NEXT_PUBLIC_GOOGLE_MAP_ID,
                zoomControlOptions: {
                  position: google.maps.ControlPosition.TOP_RIGHT,
                },
              }}
              onLoad={onLoad}
              onUnmount={onMount}
              onBoundsChanged={() => handleBoundsChanged()}
              onDragEnd={() => {
                handleBoundsChanged();
                setShowSearchAreaButton(true);
              }}
            >
              {activeLocation && (
                <InfoWindow
                  options={{
                    pixelOffset: new window.google.maps.Size(0, -40),
                  }}
                  position={{
                    lat: activeLocation?.latitude ?? 0,
                    lng: activeLocation?.longitude ?? 0,
                  }}
                  onCloseClick={() => setActiveLocation(0)}
                >
                  <div>
                    {/* @TODO, make this a component and remove the function */}
                    {mapTooltip(
                      activeLocation,
                      () => toggleFulfillmentModal(true, activeLocation, CONVEYANCE_TYPES.DELIVERY),
                      theme,
                      strings,
                      basketFulfillment,
                      theme,
                    )}
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </StyledMap>
        </StyledMapOuter>
      ) : null}
    </>
  );
};

export default LocationsMap;
